import { useCallback, useEffect, useState } from 'react';
import { FiDownload, FiEye, FiSearch, FiTarget, FiTrendingUp, FiUsers } from 'react-icons/fi';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import { Modal, Pagination, toast } from '../../components/ui';
import {
    adminApi,
    type OnboardingSurveyChartPoint,
    type OnboardingSurveyRow,
    type OnboardingSurveyStats,
} from '../../services/admin';
import styles from './Admin.module.css';
import { AdminLayout } from './AdminLayout';

const PALETTE = ['#6366f1', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#14b8a6', '#f97316', '#ec4899'];
const CHART_HEIGHT = 300;
const CHART_ROW_HEIGHT = 40;
const Y_AXIS_LABEL_MAX = 24;
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

interface ChartCardProps {
    title: string;
    children: React.ReactNode;
}
function ChartCard({ title, children }: ChartCardProps) {
    return (
        <div className={styles.chartCard}>
            <p className={styles.chartTitle}>{title}</p>
            {children}
        </div>
    );
}

function truncateLabel(label: string, max = Y_AXIS_LABEL_MAX) {
    return label.length > max ? `${label.slice(0, max - 1).trimEnd()}…` : label;
}

function renderCategoryAxisTick({ x, y, payload }: { x: number | string; y: number | string; payload: { value: string } }) {
    return (
        <text x={x} y={y} dy={4} textAnchor="end" fontSize={12} fill="#475569">
            <title>{payload.value}</title>
            {truncateLabel(payload.value)}
        </text>
    );
}

function HorizontalBarChart({ data }: { data: OnboardingSurveyChartPoint[] }) {
    if (data.length === 0) return <div className={styles.chartSkeleton}>Sem dados</div>;
    const height = Math.max(CHART_HEIGHT, data.length * CHART_ROW_HEIGHT);
    return (
        <ResponsiveContainer width="100%" height={height}>
            <BarChart data={data} layout="vertical" margin={{ top: 4, right: 24, left: 8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="label" width={150} tick={renderCategoryAxisTick} interval={0} />
                <Tooltip formatter={(value, _name, item) => [`${value} (${item.payload.pct}%)`, 'Respostas']} />
                <Bar dataKey="value" radius={[0, 6, 6, 0]} barSize={22}>
                    {data.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                </Bar>
            </BarChart>
        </ResponsiveContainer>
    );
}

export function AdminOnboardingSurvey() {
    const [stats, setStats] = useState<OnboardingSurveyStats | null>(null);
    const [statsLoading, setStatsLoading] = useState(true);

    const [rows, setRows] = useState<OnboardingSurveyRow[]>([]);
    const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 1 });
    const [tableLoading, setTableLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [selectedRow, setSelectedRow] = useState<OnboardingSurveyRow | null>(null);
    const [exportStartDate, setExportStartDate] = useState('');
    const [exportEndDate, setExportEndDate] = useState('');
    const [isExporting, setIsExporting] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(searchTerm), 400);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    useEffect(() => {
        setStatsLoading(true);
        adminApi.getOnboardingSurveyStats()
            .then(setStats)
            .catch(console.error)
            .finally(() => setStatsLoading(false));
    }, []);

    const loadRows = useCallback(async (page: number, limit: number, search: string) => {
        setTableLoading(true);
        try {
            const res = await adminApi.listOnboardingSurveys({ page, limit, search: search || undefined });
            setRows(res.data);
            setPagination(res.pagination);
        } catch (e) {
            console.error(e);
        } finally {
            setTableLoading(false);
        }
    }, []);

    useEffect(() => {
        loadRows(1, pagination.limit, debouncedSearch);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedSearch]);

    const handlePageChange = (page: number) => loadRows(page, pagination.limit, debouncedSearch);
    const handleLimitChange = (limit: number) => loadRows(1, limit, debouncedSearch);

    const handleExport = async () => {
        if (exportStartDate && exportEndDate && exportStartDate > exportEndDate) {
            toast('A data inicial não pode ser depois da data final.', 'error');
            return;
        }
        setIsExporting(true);
        try {
            await adminApi.exportOnboardingSurveys({
                startDate: exportStartDate || undefined,
                endDate: exportEndDate || undefined,
                search: debouncedSearch || undefined,
            });
        } catch (e) {
            console.error(e);
            toast('Erro ao exportar a planilha. Tente novamente.', 'error');
        } finally {
            setIsExporting(false);
        }
    };

    const formatDate = (d: string) => new Date(d).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });

    const kpis = stats?.kpis;

    return (
        <AdminLayout>
            <div className={styles.pageHeader}>
                <div className={styles.headerLeft}>
                    <h2>Pesquisa de Onboarding</h2>
                    <p>Respostas do formulário de perfil de negócio preenchido por novos usuários</p>
                </div>
            </div>

            <div className={`${styles.statsGrid} ${styles.statsGridThree}`}>
                <div className={styles.statCard}>
                    <div className={`${styles.icon} ${styles.users}`}><FiUsers size={20} /></div>
                    <div className={styles.label}>Total de Respostas</div>
                    <div className={styles.value}>{statsLoading ? '-' : kpis?.totalRespostas ?? 0}</div>
                </div>
                <div className={styles.statCard}>
                    <div className={`${styles.icon} ${styles.plans}`}><FiTrendingUp size={20} /></div>
                    <div className={styles.label}>Principal Canal de Aquisição</div>
                    <div className={styles.value}>{statsLoading ? '-' : kpis?.principalCanalAquisicao.label ?? '-'}</div>
                    {!statsLoading && kpis && (
                        <span className={styles.outroNote}>{kpis.principalCanalAquisicao.value} respostas ({kpis.principalCanalAquisicao.pct}%)</span>
                    )}
                </div>
                <div className={styles.statCard}>
                    <div className={`${styles.icon} ${styles.active}`}><FiTarget size={20} /></div>
                    <div className={styles.label}>Segmento Mais Comum</div>
                    <div className={styles.value}>{statsLoading ? '-' : kpis?.segmentoMaisComum.label ?? '-'}</div>
                    {!statsLoading && kpis && (
                        <span className={styles.outroNote}>{kpis.segmentoMaisComum.value} respostas ({kpis.segmentoMaisComum.pct}%)</span>
                    )}
                </div>
            </div>

            <div className={`${styles.chartsGrid} ${styles.chartsGridTwoCol}`}>
                <ChartCard title="Como Conheceu (canais de aquisição)">
                    {statsLoading ? <div className={styles.chartSkeleton}>Carregando...</div> : <HorizontalBarChart data={stats?.charts.comoConheceu ?? []} />}
                </ChartCard>
                <ChartCard title="Segmento do Negócio">
                    {statsLoading ? <div className={styles.chartSkeleton}>Carregando...</div> : <HorizontalBarChart data={stats?.charts.segmento ?? []} />}
                </ChartCard>
            </div>

            <div className={`${styles.chartsGrid} ${styles.chartsGridTwoCol}`}>
                <ChartCard title="Objetivo Principal">
                    {statsLoading ? <div className={styles.chartSkeleton}>Carregando...</div> : <HorizontalBarChart data={stats?.charts.objetivo ?? []} />}
                </ChartCard>
                <ChartCard title="Faturamento Anual">
                    {statsLoading ? <div className={styles.chartSkeleton}>Carregando...</div> : <HorizontalBarChart data={stats?.charts.faturamentoAnual ?? []} />}
                </ChartCard>
            </div>

            <div className={styles.filtersPanel}>
                <div className={styles.searchInput}>
                    <FiSearch size={16} className={styles.searchIcon} />
                    <input
                        type="text"
                        placeholder="Buscar por nome ou email..."
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                    />
                </div>
                <div className={styles.pageSizeSelect}>
                    <label htmlFor="onboarding-survey-page-size">Itens por página</label>
                    <select
                        id="onboarding-survey-page-size"
                        value={pagination.limit}
                        onChange={e => handleLimitChange(Number(e.target.value))}
                    >
                        {PAGE_SIZE_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                </div>

                <div className={styles.filtersDivider} />

                <div className={styles.exportDateField}>
                    <label htmlFor="onboarding-export-start">De</label>
                    <input
                        id="onboarding-export-start"
                        type="date"
                        value={exportStartDate}
                        max={exportEndDate || undefined}
                        onChange={e => setExportStartDate(e.target.value)}
                    />
                </div>
                <div className={styles.exportDateField}>
                    <label htmlFor="onboarding-export-end">Até</label>
                    <input
                        id="onboarding-export-end"
                        type="date"
                        value={exportEndDate}
                        min={exportStartDate || undefined}
                        onChange={e => setExportEndDate(e.target.value)}
                    />
                </div>
                <button
                    type="button"
                    className={styles.exportBtn}
                    onClick={handleExport}
                    disabled={isExporting}
                >
                    <FiDownload size={16} />
                    {isExporting ? 'Exportando...' : 'Exportar planilha'}
                </button>
            </div>

            <div className={styles.usersSection}>
                {tableLoading ? (
                    <div className={styles.loadingState}>Carregando...</div>
                ) : rows.length === 0 ? (
                    <div className={styles.emptyState}>
                        <FiUsers size={48} style={{ opacity: 0.3 }} />
                        <p>Nenhuma resposta encontrada.</p>
                    </div>
                ) : (
                    <table className={`${styles.usersTable} ${styles.onboardingTable}`}>
                        <thead>
                            <tr>
                                <th>Usuário</th>
                                <th>Respondido em</th>
                                <th>Segmento</th>
                                <th>Faturamento</th>
                                <th>Como Conheceu</th>
                                <th>Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(row => (
                                <tr key={row.id}>
                                    <td>
                                        <div className={styles.userInfo}>
                                            <div className={styles.userAvatar}>{row.userName.charAt(0).toUpperCase()}</div>
                                            <div className={styles.userDetails}>
                                                <div className={styles.name}>{row.userName}</div>
                                                <div className={styles.email}>{row.userEmail}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td>{formatDate(row.completedAt)}</td>
                                    <td>
                                        {row.segmento}
                                        {row.segmentoOutro && <span className={styles.outroNote}>{row.segmentoOutro}</span>}
                                    </td>
                                    <td>{row.faturamentoAnual}</td>
                                    <td>
                                        <div className={styles.tagList}>
                                            {row.comoConheceu.map((v, i) => <span key={i} className={styles.tagChip}>{v}</span>)}
                                        </div>
                                        {row.comoConheceuOutro && <span className={styles.outroNote}>Outro: {row.comoConheceuOutro}</span>}
                                    </td>
                                    <td>
                                        <button
                                            type="button"
                                            className={styles.actionBtn}
                                            title="Ver detalhes da pesquisa"
                                            onClick={() => setSelectedRow(row)}
                                        >
                                            <FiEye size={18} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
                <Pagination
                    currentPage={pagination.page}
                    totalPages={pagination.totalPages}
                    totalItems={pagination.total}
                    itemsPerPage={pagination.limit}
                    onPageChange={handlePageChange}
                />
            </div>

            <Modal
                isOpen={!!selectedRow}
                onClose={() => setSelectedRow(null)}
                title="Detalhes da Pesquisa de Onboarding"
                size="large"
            >
                {selectedRow && (
                    <>
                        <div className={styles.detailUserHeader}>
                            <div className={styles.userAvatar}>{selectedRow.userName.charAt(0).toUpperCase()}</div>
                            <div className={styles.userDetails}>
                                <div className={styles.name}>{selectedRow.userName}</div>
                                <div className={styles.email}>{selectedRow.userEmail}</div>
                                <span className={styles.outroNote}>Respondido em {formatDate(selectedRow.completedAt)}</span>
                            </div>
                        </div>

                        <div className={styles.detailGrid}>
                            <div className={`${styles.detailItem} ${styles.detailFull}`}>
                                <span className={styles.detailLabel}>Objetivo Principal</span>
                                <div className={styles.tagList}>
                                    {selectedRow.objetivo.map((v, i) => <span key={i} className={styles.tagChip}>{v}</span>)}
                                </div>
                                {selectedRow.objetivoOutro && <span className={styles.outroNote}>Outro: {selectedRow.objetivoOutro}</span>}
                            </div>

                            <div className={styles.detailItem}>
                                <span className={styles.detailLabel}>Controle Financeiro</span>
                                <span className={styles.detailValue}>{selectedRow.controleFinanceiro}</span>
                                {selectedRow.controleFinanceiroOutro && <span className={styles.outroNote}>{selectedRow.controleFinanceiroOutro}</span>}
                            </div>

                            <div className={styles.detailItem}>
                                <span className={styles.detailLabel}>Segmento do Negócio</span>
                                <span className={styles.detailValue}>{selectedRow.segmento}</span>
                                {selectedRow.segmentoOutro && <span className={styles.outroNote}>{selectedRow.segmentoOutro}</span>}
                            </div>

                            <div className={styles.detailItem}>
                                <span className={styles.detailLabel}>Faturamento Anual</span>
                                <span className={styles.detailValue}>{selectedRow.faturamentoAnual}</span>
                            </div>

                            <div className={styles.detailItem}>
                                <span className={styles.detailLabel}>Número de Funcionários</span>
                                <span className={styles.detailValue}>{selectedRow.numeroFuncionarios}</span>
                            </div>

                            <div className={`${styles.detailItem} ${styles.detailFull}`}>
                                <span className={styles.detailLabel}>Como Conheceu</span>
                                <div className={styles.tagList}>
                                    {selectedRow.comoConheceu.map((v, i) => <span key={i} className={styles.tagChip}>{v}</span>)}
                                </div>
                                {selectedRow.comoConheceuOutro && <span className={styles.outroNote}>Outro: {selectedRow.comoConheceuOutro}</span>}
                            </div>
                        </div>
                    </>
                )}
            </Modal>
        </AdminLayout>
    );
}
